import PropTypes from 'prop-types';
import React from 'react';
import {
  getTotalHoursForDateRangeFromScheduleRules,
  roundToDecimals
} from '~/modules/resourcing/common/util/scheduleUtil';
import {
  mapScaleToPeriodResolution,
  getRemainingAndAvailableValuesFunction
} from '~/modules/resourcing/common/util';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline';
import { useResourceAllocationSeriesData } from '~/modules/resourcing/common/hooks';
import { useMeContext } from '~/modules/me/useMeContext';
import { getAllocationPeriodEditorDateRangeFromAllocation } from '../ResourceAllocationPeriodEditPopover/components/ResourceAllocationEditor/getAllocationPeriodEditorDateRange';
import TaskAllocationPeriodEditPopover from '../TaskAllocationPeriodEditPopover';
import { calculateTotalSummaryHours } from '../../hooks/useResourceAllocationSeriesData';
import { useOnChangeHandlers } from './useOnChangeHandlers';

const TaskAllocationTimelineEditorOverlay = ({
  allocationPeriodEditTarget,
  handleAllocationPeriodClose,
  loadingResourceAllocation,
  onChange,
  resourceAllocationScheduleRules,
  resourceAvailabilitySummaryByPeriodMap,
  setNextPeriod,
  setPreviousPeriod,
  taskResourceUserAllocation,
  userId,
  userTaskAllocationsSummaryLoading,
  userTaskAllocationsSummaryScheduleRules
}) => {
  const {
    anchorEl,
    startDate,
    endDate,
    allocatedHours = 0,
    hasAllocationPartialEnd,
    hasAllocationPartialStart
  } = allocationPeriodEditTarget;

  const { permissionsMap } = useMeContext();

  const isCompleteResourceAllocationPermissionEnabled = Boolean(
    permissionsMap['urn:replicon-webui:completed-resource-allocation:edit']
  );

  const {
    user: { scheduleDurationByPeriodMap }
  } = taskResourceUserAllocation;

  const scheduledHours =
    scheduleDurationByPeriodMap?.[startDate.toISO()]?.hours || 0;

  const {
    periodStartDate = startDate,
    periodEndDate = endDate
  } = taskResourceUserAllocation?.scheduleRules
    ? getAllocationPeriodEditorDateRangeFromAllocation({
        endDate,
        scheduleRules: taskResourceUserAllocation.scheduleRules,
        startDate,
        isPartial: hasAllocationPartialEnd || hasAllocationPartialStart
      })
    : {};

  const totalTaskAllocatedHours = userTaskAllocationsSummaryScheduleRules
    ? getTotalHoursForDateRangeFromScheduleRules({
        start: periodStartDate,
        end: periodEndDate,
        scheduleRules: userTaskAllocationsSummaryScheduleRules
      })
    : 0;

  const resourceAllocatedHours = resourceAllocationScheduleRules
    ? getTotalHoursForDateRangeFromScheduleRules({
        start: periodStartDate,
        end: periodEndDate,
        scheduleRules: resourceAllocationScheduleRules
      })
    : 0;

  const allocatedHoursRoundedValue = roundToDecimals(allocatedHours);

  const {
    resourceAllocationSeriesData,
    loading: scheduleLoading
  } = useResourceAllocationSeriesData({
    periodResolution: mapScaleToPeriodResolution(PERIOD_SCALE_ENUM.DAYS),
    dateRange: {
      startDate: periodStartDate,
      endDate: periodEndDate
    },
    filter: {
      userIds: [userId]
    },
    includeToBeHiredAndRequested: false,
    fetchPolicy: 'no-cache',
    skip: !isCompleteResourceAllocationPermissionEnabled
  });

  const { otherProjectAllocationHours, workHours } = calculateTotalSummaryHours(
    resourceAllocationSeriesData,
    resourceAllocatedHours
  );

  const getRemainingAndAvailableValuesFunc = getRemainingAndAvailableValuesFunction(
    {
      workHours,
      scheduledHours:
        resourceAvailabilitySummaryByPeriodMap?.[startDate]?.scheduledDuration,
      otherProjectAllocationHours: otherProjectAllocationHours || 0,
      initialTaskAllocationHours: allocatedHours,
      initialProjectAllocationHours: resourceAllocatedHours,
      initialTotalTaskAllocationHours: totalTaskAllocatedHours,
      isCompleteResourceAllocationPermissionEnabled
    }
  );

  const { onAllocationChange } = useOnChangeHandlers({
    taskResourceUserAllocation,
    periodStartDate,
    periodEndDate,
    onChange,
    periodTaskAllocatedHours: allocatedHours
  });

  return (
    <TaskAllocationPeriodEditPopover
      anchorEl={anchorEl}
      periodDetails={allocationPeriodEditTarget}
      allocatedHours={allocatedHoursRoundedValue}
      handleAllocationPeriodClose={handleAllocationPeriodClose}
      setNextPeriod={setNextPeriod}
      onAllocationChange={onAllocationChange}
      setPreviousPeriod={setPreviousPeriod}
      scheduledHours={scheduledHours}
      taskResourceUserAllocation={taskResourceUserAllocation}
      isLoading={
        userTaskAllocationsSummaryLoading ||
        loadingResourceAllocation ||
        scheduleLoading
      }
      getRemainingAndAvailableValuesFunc={getRemainingAndAvailableValuesFunc}
      initialProjectAllocationHours={resourceAllocatedHours}
    />
  );
};

TaskAllocationTimelineEditorOverlay.propTypes = {
  allocationPeriodEditTarget: PropTypes.object,
  handleAllocationPeriodClose: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  loadingResourceAllocation: PropTypes.bool,
  resourceAllocationScheduleRules: PropTypes.array,
  setNextPeriod: PropTypes.func,
  setPreviousPeriod: PropTypes.func,
  taskResourceUserAllocation: PropTypes.object,
  userId: PropTypes.string,
  userTaskAllocationsSummaryLoading: PropTypes.bool,
  userTaskAllocationsSummaryScheduleRules: PropTypes.array,
  resourceAvailabilitySummaryByPeriodMap: PropTypes.object
};

export default TaskAllocationTimelineEditorOverlay;
